import React from 'react';
import {
  makeStyles,
  shorthands,
  Text,
  Spinner,
  Skeleton,
  SkeletonItem,
} from '@fluentui/react-components';

const useStyles = makeStyles({
  centeredSpinner: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    ...shorthands.padding('48px'),
    ...shorthands.gap('16px'),
  },
  inlineSpinner: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
    ...shorthands.padding('8px', '0'),
  },
  tableLoadingSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('8px'),
    ...shorthands.padding('16px'),
  },
  skeletonRow: {
    display: 'flex',
    ...shorthands.gap('16px'),
    alignItems: 'center',
  },
  cardLoadingSkeleton: {
    display: 'flex',
    flexDirection: 'column',
    ...shorthands.gap('12px'),
    ...shorthands.padding('24px'),
  },
  cardSkeletonHeader: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('12px'),
  },
  statusLoadingSkeleton: {
    display: 'flex',
    alignItems: 'center',
    ...shorthands.gap('8px'),
  },
});

interface CenteredLoadingProps {
  message?: string;
  size?: 'small' | 'medium' | 'large' | 'extra-large';
}

/**
 * Centered loading spinner for full page/section loading
 */
export const CenteredLoading: React.FC<CenteredLoadingProps> = ({ 
  message = 'Loading...', 
  size = 'large' 
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.centeredSpinner} data-testid="centered-loading">
      <Spinner size={size} data-testid="centered-loading-spinner" />
      <Text style={{ color: '#666', fontSize: '14px' }} data-testid="centered-loading-message">{message}</Text>
    </div>
  );
};

interface InlineLoadingProps {
  message?: string;
  size?: 'tiny' | 'extra-small' | 'small' | 'medium' | 'large' | 'extra-large';
}

/**
 * Inline loading spinner for smaller components
 */
export const InlineLoading: React.FC<InlineLoadingProps> = ({ 
  message = 'Loading...', 
  size = 'small' 
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.inlineSpinner} data-testid="inline-loading">
      <Spinner size={size} data-testid="inline-loading-spinner" />
      <Text style={{ fontSize: '14px', color: '#666' }} data-testid="inline-loading-message">{message}</Text>
    </div>
  );
};

interface TableLoadingSkeletonProps {
  rows?: number;
  columns?: number;
}

/**
 * Skeleton loader for table content
 */
export const TableLoadingSkeleton: React.FC<TableLoadingSkeletonProps> = ({ 
  rows = 3, 
  columns = 4 
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.tableLoadingSkeleton} data-testid="table-loading-skeleton">
      {Array.from({ length: rows }, (_, rowIndex) => (
        <div key={rowIndex} className={styles.skeletonRow} data-testid={`table-loading-skeleton-row-${rowIndex}`}>
          {Array.from({ length: columns }, (_, colIndex) => (
            <Skeleton key={colIndex} data-testid={`table-loading-skeleton-cell-${rowIndex}-${colIndex}`}>
              <SkeletonItem 
                style={{ 
                  width: colIndex === 0 ? '200px' : '120px',
                  height: '16px' 
                }}
                data-testid={`table-loading-skeleton-item-${rowIndex}-${colIndex}`}
              />
            </Skeleton>
          ))}
        </div>
      ))}
    </div>
  );
};

interface CardLoadingSkeletonProps {
  hasIcon?: boolean;
  hasSubtitle?: boolean;
  hasActions?: boolean;
}

/**
 * Skeleton loader for card content
 */
export const CardLoadingSkeleton: React.FC<CardLoadingSkeletonProps> = ({ 
  hasIcon = true,
  hasSubtitle = true,
  hasActions = false,
}) => {
  const styles = useStyles();
  
  return (
    <div className={styles.cardLoadingSkeleton} data-testid="card-loading-skeleton">
      <div className={styles.cardSkeletonHeader} data-testid="card-loading-skeleton-header">
        {hasIcon && (
          <Skeleton data-testid="card-loading-skeleton-icon">
            <SkeletonItem style={{ width: '24px', height: '24px', borderRadius: '50%' }} data-testid="card-loading-skeleton-icon-item" />
          </Skeleton>
        )}
        <div style={{ flex: 1 }} data-testid="card-loading-skeleton-main">
          <Skeleton data-testid="card-loading-skeleton-title">
            <SkeletonItem style={{ width: '180px', height: '20px' }} data-testid="card-loading-skeleton-title-item" />
          </Skeleton>
          {hasSubtitle && (
            <div style={{ marginTop: '8px' }} data-testid="card-loading-skeleton-subtitle-container">
              <Skeleton data-testid="card-loading-skeleton-subtitle">
                <SkeletonItem style={{ width: '240px', height: '14px' }} data-testid="card-loading-skeleton-subtitle-item" />
              </Skeleton>
            </div>
          )}
        </div>
        {hasActions && (
          <Skeleton data-testid="card-loading-skeleton-action">
            <SkeletonItem style={{ width: '24px', height: '24px' }} data-testid="card-loading-skeleton-action-item" />
          </Skeleton>
        )}
      </div>
    </div>
  );
};

/**
 * Loading skeleton for status badges/indicators
 */
export const StatusLoadingSkeleton: React.FC = () => {
  const styles = useStyles();
  
  return (
    <div className={styles.statusLoadingSkeleton} data-testid="status-loading-skeleton">
      <Skeleton data-testid="status-loading-skeleton-wrapper">
        <SkeletonItem style={{ width: '80px', height: '24px', borderRadius: '12px' }} data-testid="status-loading-skeleton-item" />
      </Skeleton>
    </div>
  );
};

interface ConditionalLoadingProps {
  loading: boolean;
  error?: any;
  children: React.ReactNode;
  loadingComponent?: React.ReactNode;
  errorComponent?: React.ReactNode;
}

/**
 * Wrapper component that conditionally shows loading, error, or content
 */
export const ConditionalLoading: React.FC<ConditionalLoadingProps> = ({
  loading,
  error,
  children,
  loadingComponent,
  errorComponent,
}) => {
  if (loading) {
    return <>{loadingComponent || <CenteredLoading />}</>;
  }
  
  if (error) {
    return <>{errorComponent || (
      <div style={{ padding: '24px', textAlign: 'center', color: '#dc2626' }} data-testid="conditional-loading-error">
        <Text data-testid="conditional-loading-error-message">Error: {error?.message || 'Something went wrong'}</Text>
      </div>
    )}</>;
  }
  
  return <>{children}</>;
};

interface LoadingOverlayProps {
  loading: boolean;
  message?: string;
  children: React.ReactNode;
}

/**
 * Loading overlay that shows on top of content
 */
export const LoadingOverlay: React.FC<LoadingOverlayProps> = ({
  loading,
  message = 'Loading...',
  children,
}) => {
  return (
    <div style={{ position: 'relative' }} data-testid="loading-overlay-container">
      {children}
      {loading && (
        <div
          data-testid="loading-overlay"
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: 'rgba(255, 255, 255, 0.8)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            zIndex: 10,
          }}
        >
          <div style={{ textAlign: 'center' }} data-testid="loading-overlay-content">
            <Spinner size="large" data-testid="loading-overlay-spinner" />
            <Text style={{ marginTop: '16px', color: '#666' }} data-testid="loading-overlay-message">{message}</Text>
          </div>
        </div>
      )}
    </div>
  );
};

export default {
  CenteredLoading,
  InlineLoading,
  TableLoadingSkeleton,
  CardLoadingSkeleton,
  StatusLoadingSkeleton,
  ConditionalLoading,
  LoadingOverlay,
};